import { api, ApiError } from '../../lib/api';
import type {
  TaskTemplate,
  TaskTemplateItem,
  TaskTemplateScope,
  TaskTemplateWithItems,
} from '../../types/taskTemplate';
import type { CurrentUser } from '../../types/user';

// Client API for the settings module (task-template library, Slice B §2/§3).
// Thin fetch wrappers over /api/task-templates; imported only by components
// inside src/modules/settings/. The plans module's apply flow owns its own
// wrappers in plans/api.ts (each client module owns its api.ts — same
// module-boundary discipline as listBrandCampaigns / listUsers).

export type TemplateCreatePayload = {
  name: string;
  description: string | null;
  scope: TaskTemplateScope;
  brandAccountId: string | null;
};

export type TemplateUpdatePayload = Partial<{
  name: string;
  description: string | null;
  position: number;
}>;

export type ItemCreatePayload = {
  title: string;
  description: string | null;
  relativeDueDays: number | null;
  defaultTeam: string | null;
};

export type ItemUpdatePayload = Partial<{
  title: string;
  description: string | null;
  relativeDueDays: number | null;
  defaultTeam: string | null;
  position: number;
}>;

async function maybe<T>(fn: () => Promise<T>): Promise<T | null> {
  try {
    return await fn();
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

export async function listTemplates(): Promise<TaskTemplate[]> {
  const res = await api<{ templates: TaskTemplate[] }>('/api/task-templates');
  return res.templates;
}

export async function getTemplate(
  id: string,
): Promise<TaskTemplateWithItems | null> {
  return maybe(async () => {
    const res = await api<{ template: TaskTemplateWithItems }>(
      `/api/task-templates/${id}`,
    );
    return res.template;
  });
}

export async function createTemplate(
  payload: TemplateCreatePayload,
): Promise<TaskTemplate> {
  const res = await api<{ template: TaskTemplate }>('/api/task-templates', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return res.template;
}

export async function updateTemplate(
  id: string,
  patch: TemplateUpdatePayload,
): Promise<TaskTemplate> {
  const res = await api<{ template: TaskTemplate }>(
    `/api/task-templates/${id}`,
    { method: 'PATCH', body: JSON.stringify(patch) },
  );
  return res.template;
}

export type DeleteTemplateResult = {
  deletedTemplateId: string;
  deletedItemCount: number;
};

export async function deleteTemplate(
  id: string,
): Promise<DeleteTemplateResult> {
  return api<DeleteTemplateResult>(`/api/task-templates/${id}`, {
    method: 'DELETE',
  });
}

export async function createItem(
  templateId: string,
  payload: ItemCreatePayload,
): Promise<TaskTemplateItem> {
  const res = await api<{ item: TaskTemplateItem }>(
    `/api/task-templates/${templateId}/items`,
    { method: 'POST', body: JSON.stringify(payload) },
  );
  return res.item;
}

export async function updateItem(
  templateId: string,
  itemId: string,
  patch: ItemUpdatePayload,
): Promise<TaskTemplateItem> {
  const res = await api<{ item: TaskTemplateItem }>(
    `/api/task-templates/${templateId}/items/${itemId}`,
    { method: 'PATCH', body: JSON.stringify(patch) },
  );
  return res.item;
}

export async function deleteItem(
  templateId: string,
  itemId: string,
): Promise<void> {
  await api<{ ok: true }>(
    `/api/task-templates/${templateId}/items/${itemId}`,
    { method: 'DELETE' },
  );
}

export async function reorderItems(
  templateId: string,
  itemIds: string[],
): Promise<TaskTemplateItem[]> {
  const res = await api<{ items: TaskTemplateItem[] }>(
    `/api/task-templates/${templateId}/items/order`,
    { method: 'PATCH', body: JSON.stringify({ itemIds }) },
  );
  return res.items;
}

// Self-editable AM profile (Settings → My Profile). PATCH /api/me/profile with
// only the keys the form changed; null clears a field. Returns the refreshed
// CurrentUser so the caller can prime the ['me'] cache. Settings owns its
// api.ts and hits the shared /api/me endpoint directly — same posture as
// listUsers hitting /api/users below.
export type ProfileUpdatePayload = Partial<{
  fullName: string | null;
  jobTitle: string | null;
  phoneNumber: string | null;
  emailSignature: string | null;
}>;

export async function updateMyProfile(
  payload: ProfileUpdatePayload,
): Promise<CurrentUser> {
  const res = await api<{ user: CurrentUser }>('/api/me/profile', {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  return res.user;
}

// Lightweight user list for the item form's default-team dropdown (§3.6).
// Teams are derived from distinct User.team values (§5.3) via lib/teams.
// Hits the shared /api/users endpoint directly — settings owns its api.ts.
export async function listUsers(): Promise<CurrentUser[]> {
  const res = await api<{ users: CurrentUser[] }>('/api/users');
  return res.users;
}

// ---- Settings -> Users (admin only) ----
export type ManagedUser = import('../../lib/usersView').UserRow;

export async function listManagedUsers(): Promise<ManagedUser[]> {
  const res = await api<{ users: ManagedUser[] }>('/api/admin/users');
  return res.users;
}

export async function patchManagedUser(
  uid: string,
  patch: { jobRole?: string | null; role?: 'admin' | 'staff' },
): Promise<void> {
  await api(`/api/admin/users/${encodeURIComponent(uid)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export async function disableManagedUser(uid: string): Promise<{ tokensRevoked: boolean }> {
  return api<{ tokensRevoked: boolean }>(`/api/admin/users/${encodeURIComponent(uid)}/disable`, {
    method: 'POST',
  });
}

export async function enableManagedUser(uid: string): Promise<void> {
  await api(`/api/admin/users/${encodeURIComponent(uid)}/enable`, { method: 'POST' });
}
